// Колоните на „История на маси".
//
// Id-тата НЕ са произволни: съвпадат едно към едно с `BillsHistorySortBy` на
// BE-то, за да може сортирането да се вдигне до сървъра без таблица за
// превод. Колоната „Сервитьор" няма сорт, защото името му живее в ДРУГА база
// (org) и BE-то няма по какво да подреди.
//
// Филтрите тук са в СЪРВЪРЕН режим, затова чеклистите носят изрични `options`
// от справочниците — фасетите биха показали само стойностите на текущата
// страница и списъкът би лъгал.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import {
  fiscalAmountOf,
  type BillHistory,
} from "../../../../api/reportsBillsHistory";
import type { PaymentMethod } from "../../../../api/paymentMethods";
import type { UserListItemOut } from "../../../../api/auth";
import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import type {
  ChecklistOption,
  DotCellValue,
} from "../../../../components/dataTable";
import { fmtEuro } from "../../../../utils/currency";
import {
  cancelledCountOf,
  cancelledTotalOf,
  discountOf,
  itemsQuantityOf,
} from "./billsHistoryMath";

/**
 * Ред с ВЕЧЕ РАЗРЕШЕНИ имена. Имената НЕ се вадят от справочник вътре в
 * `accessorFn`: TanStack кешира стойността на клетката за живота на `data`, а
 * React Query пази същата референция при непроменен отговор — справочник,
 * пристигнал по-късно, тогава никога не се появява в клетката.
 */
export type NamedBillHistory = BillHistory & {
  waiterName: string;
  paymentName: string;
};

const columnHelper = createColumnHelper<NamedBillHistory>();

/** Смесеното плащане (id 3) получава втори ред „в брой · карта". */
const MIXED_PAYMENT_METHOD_ID = 3;

type Options = {
  users: UserListItemOut[];
  paymentMethods: PaymentMethod[];
  /** Периодът обхваща повече от един ден → в клетките с час излиза и датата. */
  isMultiDay: boolean;
  /**
   * false = архивиран ресторант И сесия БЕЗ втори код (`session_access` от
   * BE-то; при неархивиран ресторант винаги е true). BE-то и без това не
   * пуска такива редове до екрана, но контурната точка се гейтва изрично:
   * КПИ-то вече е, а асиметрията „едното пази, другото не" е капан за
   * следваща промяна. Колоната „№ сметка" при false ИЗОБЩО не се създава —
   * нито в таблицата, нито в панела „Колони".
   */
  sessionAccess: boolean;
  /** `fiscal_split` от BE — за „Сума ФП" (виж `fiscalAmountOf`). */
  fiscalSplit: boolean;
};

export const useBillsHistoryColumns = ({
  users,
  paymentMethods,
  isMultiDay,
  sessionAccess,
  fiscalSplit,
}: Options) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");

  const waiterOptions = useMemo<ChecklistOption[]>(
    () =>
      users
        .map((user) => ({
          value: String(user.id),
          label: user.names || user.username || String(user.id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  const paymentOptions = useMemo<ChecklistOption[]>(
    () =>
      paymentMethods.map((method) => ({
        value: String(method.id),
        label: isEnglish ? method.name_en : method.name_bg,
      })),
    [paymentMethods, isEnglish],
  );

  // Стойностите са низовете на булевото, защото точно те се вдигат до
  // `in_invoice` в заявката (както id-тата на сервитьорите и на методите).
  const invoiceOptions = useMemo<ChecklistOption[]>(
    () => [
      { value: "true", label: t("common.yes") },
      { value: "false", label: t("common.no") },
    ],
    [t],
  );

  return useMemo(
    () => [
      // Справката показва само ПРИКЛЮЧЕНИ сметки, затова точката вече носи
      // един-единствен смисъл: издаден ли е фискален бон. Запълнена = да,
      // с контур = не. Без надпис и без втора колона — смисълът е четим от
      // самия ред, защото колоната „Бон №" е с тире.
      columnHelper.accessor(
        (bill): DotCellValue => ({
          tone: "brand",
          label: t("reportsPage.statusClosedOne"),
          outline: sessionAccess && !bill.receipt_number,
        }),
        {
          id: "status",
          header: "",
          size: 34,
          enableResizing: false,
          enableSorting: false,
          meta: {
            // Заглавието е празно по дизайн (точка без надпис), затова
            // панелът и менюто ползват този етикет.
            label: t("reportsPage.status"),
            cell: { kind: "dot" },
            alwaysVisible: true,
            excel: { header: t("reportsPage.status"), format: "text" },
          },
        },
      ),

      // Номерът на сметката в базата — по него се сверява с бекофиса и с
      // подадена рекламация. Изгасена по подразбиране: рядко трябва, но
      // когато трябва, няма откъде другаде да се вземе.
      //
      // САМО при `sessionAccess`: при архивиран ресторант без втори код
      // номерът не бива нито да се вижда, нито да може да се включи от
      // панела „Колони" — затова колоната не се създава изобщо, а не се
      // крие. При неархивиран ресторант BE-то връща `session_access: true`
      // и колоната си е налична за всички.
      //
      // Сортира се СЪРВЪРНО, като всичко останало в тази справка: id-то на
      // колоната отива направо в `sort_by`, а BE-то подрежда целия период,
      // не текущата страница.
      ...(sessionAccess
        ? [
            columnHelper.accessor((bill) => bill.id, {
              id: "bill_id",
              header: t("reportsPage.billIdShort"),
              size: 84,
              meta: {
                cell: { kind: "num" },
                align: "right",
                defaultHidden: true,
                excel: {
                  header: t("reportsPage.billIdShort"),
                  format: "number",
                },
              },
            }),
          ]
        : []),

      columnHelper.accessor((bill) => bill.name, {
        id: "name",
        header: t("reportsPage.table"),
        size: 96,
        meta: {
          cell: { kind: "text" },
          alwaysVisible: true,
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor((bill) => bill.waiterName, {
        id: "waiter",
        header: t("reportsPage.waiter"),
        size: 130,
        // BE-то няма име на сервитьор в тази база (идва от org базата),
        // затова не може и да сортира по него.
        enableSorting: false,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: waiterOptions },
          excel: { format: "text" },
        },
      }),

      // Броят гости от „Маркиране" (`bills.guests`); невъведеният е тире.
      // Валиден `sort_by`, а „Общо" идва от `totals.guests_total`.
      columnHelper.accessor((bill) => bill.guests ?? null, {
        id: "guests",
        header: t("reportsPage.guestsCount"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          align: "right",
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((bill) => bill.created_at, {
        id: "created_at",
        header: t("reportsPage.opened"),
        size: 104,
        meta: {
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "auto" },
          excel: { format: "date" },
        },
      }),

      columnHelper.accessor((bill) => bill.closed_at, {
        id: "closed_at",
        header: t("reportsPage.closed"),
        size: 104,
        meta: {
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "auto" },
          hideBelow: "tablet",
          excel: { format: "date" },
        },
      }),

      columnHelper.accessor((bill) => bill.duration_minutes, {
        id: "duration_minutes",
        header: t("reportsPage.durationShort"),
        size: 104,
        meta: {
          cell: { kind: "min" },
          align: "right",
          hideBelow: "tablet",
          excel: { format: "number" },
        },
      }),

      // Двата БРОЯЧА (подавания към кухнята и сторнирани редове) са изгасени
      // по подразбиране — редът и без тях се чете, но когато управителят ги
      // търси, няма откъде другаде да ги вземе. И двата са валиден `sort_by`.
      columnHelper.accessor((bill) => bill.statistics?.total_orders ?? 0, {
        id: "total_orders",
        header: t("reportsPage.ordersShort"),
        size: 84,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((bill) => cancelledCountOf(bill), {
        id: "cancelled_items",
        header: t("reportsPage.cancelledCountShort"),
        size: 96,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          defaultHidden: true,
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((bill) => itemsQuantityOf(bill), {
        id: "items_quantity",
        header: t("reportsPage.itemsShort"),
        size: 72,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          hideBelow: "narrow",
          excel: { format: "number" },
        },
      }),

      columnHelper.accessor((bill) => discountOf(bill), {
        id: "total_discount",
        header: t("reportsPage.discount"),
        size: 110,
        meta: {
          cell: { kind: "money", negative: true, mutedZero: true },
          total: "sum",
          hideBelow: "narrow",
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor((bill) => cancelledTotalOf(bill), {
        id: "cancelled_total",
        header: t("reportsPage.cancelled"),
        size: 110,
        meta: {
          cell: { kind: "money", negative: true, mutedZero: true },
          total: "sum",
          hideBelow: "narrow",
          excel: { format: "money" },
        },
      }),

      columnHelper.accessor(
        (bill) => {
          if (bill.status === "open") return { primary: "", secondary: null };
          const name = bill.paymentName;
          if (bill.payment_method_id !== MIXED_PAYMENT_METHOD_ID) {
            return { primary: name, secondary: null };
          }
          const cash = parseDecimal(bill.mixed_cash_amount);
          const card = parseDecimal(bill.mixed_card_amount);
          return {
            primary: name,
            secondary: `${cash.toFixed(2)} · ${card.toFixed(2)}`,
          };
        },
        {
          id: "payment_method_name",
          header: t("reportsPage.payment"),
          size: 118,
          meta: {
            cell: { kind: "text", lines: 2 },
            filter: { kind: "checklist", options: paymentOptions },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      columnHelper.accessor((bill) => bill.receipt_number ?? "", {
        id: "receipt_number",
        header: t("reportsPage.receiptNumber"),
        size: 104,
        meta: {
          cell: { kind: "text" },
          align: "right",
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Възстановявана ли е сметката (отворена наново със същото id след
      // приключване) — `restores_count` идва готово от BE-то. Тих признак
      // като фактурата: стрелка при „да", тире при „не"; при повече от едно
      // възстановяване — и броят. Подробностите са в „История на операциите".
      //
      // БЕЗ сортиране: `BillsHistorySortBy` няма такъв ключ.
      columnHelper.accessor(
        (bill) =>
          (bill.restores_count ?? 0) > 0 ? t("common.yes") : t("common.no"),
        {
          id: "restored",
          header: t("reportsPage.restored"),
          size: 96,
          enableSorting: false,
          cell: (context) => {
            const count = context.row.original.restores_count ?? 0;
            return (
              <span
                className={`font-pos-mono text-[12.5px] font-bold ${
                  count > 0 ? "text-info-deep" : "text-faint"
                }`}
              >
                <span aria-hidden>
                  {count > 1 ? `↺ ×${count}` : count === 1 ? "↺" : EM_DASH}
                </span>
                <span className="sr-only">{context.getValue()}</span>
              </span>
            );
          },
          meta: {
            align: "center",
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      // Издадена ли е фактура за тази сметка — `is_invoiced` идва готово от
      // BE-то (prefetch към `invoice_bills`). Признакът е тих: отметка при
      // „да", тире при „не", без нито една дума в клетката.
      //
      // БЕЗ сортиране: подредбата на тази справка е СЪРВЪРНА, а
      // `BillsHistorySortBy` няма такъв ключ — `sort_by=is_invoiced` е 422.
      columnHelper.accessor(
        (bill) => (bill.is_invoiced ? t("common.yes") : t("common.no")),
        {
          id: "is_invoiced",
          header: t("reportsPage.invoice"),
          size: 86,
          enableSorting: false,
          // Клетката се рисува тук, а не през `meta.cell`: СТОЙНОСТТА на
          // колоната е думата „Да" / „Не" (тя е това, което върши работа в
          // Excel и на екран за четец), а видимото е отметка.
          cell: (context) => {
            const invoiced = context.row.original.is_invoiced === true;
            return (
              <span
                className={`font-pos-mono text-[12.5px] font-bold ${
                  invoiced ? "text-brand-deep-ink" : "text-faint"
                }`}
              >
                <span aria-hidden>{invoiced ? "✓" : EM_DASH}</span>
                <span className="sr-only">{context.getValue()}</span>
              </span>
            );
          },
          meta: {
            align: "center",
            filter: { kind: "checklist", options: invoiceOptions },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      columnHelper.accessor((bill) => parseDecimal(bill.total_amount), {
        id: "total_amount",
        header: t("reportsPage.amount"),
        size: 118,
        // Изцяло сторнирана сметка иначе е неразличима от сметка с 0.00 € по
        // друга причина: над нулата стои зачеркнато това, което е щяла да
        // струва. Стойността на клетката остава ЧИСЛО — редът „Общо" и Excel
        // четат нея.
        cell: (context) => {
          const amount = context.getValue();
          const bill = context.row.original;
          const voided = cancelledTotalOf(bill);
          const fullyVoided =
            voided > 0 && itemsQuantityOf(bill) === 0 && amount === 0;
          return (
            <span className="flex flex-col items-end leading-[1.2]">
              {fullyVoided ? (
                <span className="font-pos-mono text-[10.5px] font-semibold tabular-nums text-faint line-through">
                  {fmtEuro(voided)}
                </span>
              ) : null}
              <span
                className={`font-pos-mono text-[12.5px] font-bold tabular-nums ${
                  fullyVoided ? "text-brand-deep-ink" : "text-ink"
                }`}
              >
                {fmtEuro(amount)}
              </span>
            </span>
          );
        },
        meta: {
          total: "sum",
          // Редът „Общо" няма зачеркната стойност — там клетката е обикновена
          // сума и я описва `totalCell` (`cell` тук е собственият рендер).
          totalCell: { kind: "money", strong: true },
          align: "right",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),

      // „Сума ФП" — до „Сума", както в „Продажби по периоди". Без код 2 (или в
      // неархивиран ресторант) е равна на „Сума"; с код 2 — само сметките с бон.
      columnHelper.accessor((bill) => fiscalAmountOf(bill, fiscalSplit), {
        id: "fiscal_amount",
        header: t("reportsPage.fiscalAmount"),
        size: 118,
        meta: {
          cell: { kind: "money", mutedZero: true },
          total: "sum",
          hideBelow: "tablet",
          excel: { format: "money" },
        },
      }),
    ],
    [
      t,
      isMultiDay,
      sessionAccess,
      fiscalSplit,
      waiterOptions,
      paymentOptions,
      invoiceOptions,
    ],
  );
};

/** Показваната стойност на клетка „Бон №", когато липсва номер. */
export const receiptOrDash = (value: string | null | undefined) =>
  value || EM_DASH;
